/* import __COLOCATED_TEMPLATE__ from './additional-settings-sidedrawer.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';
import type LivePersonConfiguration from 'embercom/models/standalone/live-person-configuration';
import type Token from 'embercom/models/custom-authentication/token';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';

interface Args {
  isOpen: boolean;
  onClose: () => void;
  closeOnSave: () => void;
  livePersonConfig: LivePersonConfiguration;
}

export default class StandaloneLivePersonAdditionalSettingsSidedrawer extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked customAuthenticationTokens: Token[] = [];
  @tracked selectedTokenId = this.args.livePersonConfig.tokenId;
  @tracked isSaving = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadCustomAuthenticationTokens).perform();
  }

  get tokenList() {
    return [
      {
        items: this.customAuthenticationTokens
          .filter((token) => token.tokenType === 'user')
          .map((token) => ({
            value: token.id,
            text: token.name,
          })),
      },
    ];
  }

  get selectedToken() {
    return this.customAuthenticationTokens?.find((token) => token.id === this.selectedTokenId);
  }

  get tokenFooterActions() {
    return [
      {
        text: this.intl.t('standalone.deploy.api.live_person.user_token_mapping.link_user_token'),
        icon: 'new',
        action: () =>
          safeWindowOpen(this.router.urlFor('apps.app.settings.app-settings.authentication.new')),
      },
    ];
  }

  get hasChanges() {
    let tokenIdChanged = this.selectedTokenId !== this.args.livePersonConfig.tokenId;

    let modelHasChanges = this.args.livePersonConfig.hasDirtyAttributes;

    return tokenIdChanged || modelHasChanges;
  }

  @action onTokenSelected(tokenId: string) {
    this.selectedTokenId = tokenId;
  }

  @action onEscalationSkillChange(value: string) {
    this.args.livePersonConfig.escalationSkill = value;
  }

  @action onDisableJwtToggle() {
    this.args.livePersonConfig.disableJwtVerification =
      !this.args.livePersonConfig.disableJwtVerification;
  }

  @action async saveSettings() {
    this.isSaving = true;

    try {
      let payload = this.buildPayload();

      if (Object.keys(payload).length > 0) {
        await ajax({
          url: `/ember/standalone/live_person_configurations/${this.args.livePersonConfig.id}/update_additional_settings`,
          type: 'PATCH',
          data: JSON.stringify(payload),
        });

        if (this.selectedTokenId !== this.args.livePersonConfig.tokenId) {
          this.args.livePersonConfig.tokenId = this.selectedTokenId;
        }
      }

      this.args.closeOnSave();
    } catch (error) {
      console.error('Error updating LivePerson configuration:', error);
    } finally {
      this.isSaving = false;
    }
  }

  private buildPayload(): Record<string, any> {
    let payload: Record<string, any> = {};

    if (this.selectedTokenId !== this.args.livePersonConfig.tokenId) {
      payload.token_id = this.selectedTokenId || null;
    }

    let changedAttributes = this.args.livePersonConfig.changedAttributes();

    if (changedAttributes.escalationSkill) {
      payload.escalation_skill = this.args.livePersonConfig.escalationSkill || '';
    }

    if (changedAttributes.disableJwtVerification) {
      payload.disable_jwt_verification = this.args.livePersonConfig.disableJwtVerification || false;
    }

    return payload;
  }

  @task({ drop: true })
  *loadCustomAuthenticationTokens(): TaskGenerator<void> {
    this.customAuthenticationTokens = yield this.store.query('custom-authentication/token', {
      with_app_package_tokens: false,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::LivePerson::AdditionalSettingsSidedrawer': typeof StandaloneLivePersonAdditionalSettingsSidedrawer;
    'standalone/live-person/additional-settings-sidedrawer': typeof StandaloneLivePersonAdditionalSettingsSidedrawer;
  }
}
